import React, { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useLocation, Outlet } from 'react-router-dom'
import { ThemeProvider } from '@mui/material/styles'
import { setShowSideMenuText, setSideBarWidth } from 'state/ui'
import { useMediaQuery, Grid, CssBaseline } from '@mui/material'
import { theme, getTheme } from 'themes/application'
import AppBar from './AppBar'
import TabsLayout from './TabsLayout'
import DrawerLayout from './DrawerLayout'
import CategoryNav from '../generic/navigation/CategoryNav'

// New imports for hotkeys and barcode scanning
import HotkeyModal from 'components/generic/HotkeyModal'
import { useHotkeyNavigation } from 'utils/custom-hooks/useHotkeyNavigation'
import { useBarcodeScanner } from 'utils/custom-hooks/useBarcodeScanner'

const FullHeightGrid = styled(Grid)`
  min-height: 100vh;
`
const AppLayout = ({ routes, appBarConfig }) => {
  const dispatch = useDispatch()
  const { showSideMenuText } = useSelector(state => state.ui)
  const [useCategoryNav, setUseCategoryNav] = useState(() => {
    const savedValue = localStorage.getItem('useCategoryNav')
    return savedValue !== null ? savedValue === 'true' : false
  })
  const [themeType, setThemeType] = useState(localStorage.getItem('themePreference') || 'light')

  // Add this effect to set initial localStorage value
  useEffect(() => {
    if (localStorage.getItem('useCategoryNav') === null) {
      localStorage.setItem('useCategoryNav', 'false')
    }
  }, [])

  // Theme toggle handler
  const toggleTheme = useCallback(() => {
    const newTheme = themeType === 'light' ? 'dark' : 'light'
    setThemeType(newTheme)
    localStorage.setItem('themePreference', newTheme)
  }, [themeType])

  const currentTheme = getTheme(themeType)

  useEffect(() => {
    const root = document.documentElement
    if (themeType === 'dark') {
      root.style.setProperty('--link-color', 'primary.main')
      root.style.setProperty('--link-hover-color', 'white')
      root.style.setProperty('--outlined-input-bg', 'hsla(220, 15%, 18%, 1)') // replace with the color for dark mode
    } else {
      root.style.setProperty('--link-color', 'primary.main')
      root.style.setProperty('--link-hover-color', 'primary.dark')
      root.style.setProperty('--outlined-input-bg', 'white')
    }
  }, [themeType])

  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isLargeScreenSize = useMediaQuery(theme.breakpoints.up('lg'))
  const { pathname } = useLocation()
  const isSettings = pathname.indexOf('/app/settings') > -1

  useEffect(() => {
    if (!window.localStorage.getItem('showSideMenuText')) {
      dispatch(setShowSideMenuText(isLargeScreenSize))
    }
  }, [isLargeScreenSize])

  const toggleSideMenuText = () => {
    window.localStorage.setItem('showSideMenuText', showSideMenuText ? 'false' : 'true')
    dispatch(setShowSideMenuText(!showSideMenuText))
  }

  useEffect(() => {
    dispatch(setSideBarWidth(isMobile ? 0 : document?.getElementById('nav-side-bar')?.offsetWidth || 0))
  }, [isMobile, showSideMenuText])

  // Navigation style toggle handler
  const toggleNavStyle = useCallback(() => {
    const newValue = !useCategoryNav
    setUseCategoryNav(newValue)
    localStorage.setItem('useCategoryNav', newValue.toString())
  }, [useCategoryNav])

  // **Move the hooks here so they always remain mounted**
  const { hotkeyModalOpen, setHotkeyModalOpen, accessibleMenuOptions } = useHotkeyNavigation()
  useBarcodeScanner()

  const renderContent = () => {
    if (isSettings) {
      return (
        <TabsLayout
          tabs={routes}
          tabOrientation="vertical"
          contentClassName="main-content-container"
          isSideMenu
          isSettings={isSettings}
          showContent
        />
      )
    }

    if (isMobile && !useCategoryNav) {
      return <DrawerLayout tabs={routes} />
    }

    return (
      <TabsLayout
        tabs={routes}
        tabOrientation="vertical"
        contentClassName="main-content-container"
        isSideMenu
        isSettings={isSettings}
        showContent
      />
    )
  }

  return (
    <ThemeProvider theme={currentTheme}>
      <CssBaseline />
      <FullHeightGrid
        container
        flexDirection="column"
        sx={{
          overflowX: 'clip',
          scrollbarGutter: 'stable',
        }}
      >
        {!useCategoryNav || isSettings ? (
          <Grid item>
            <AppBar
              title={appBarConfig.title}
              toggleTheme={toggleTheme}
              currentTheme={themeType}
              showNotifications={appBarConfig.showNotifications}
              showLocationSelect={appBarConfig.showLocationSelect}
              showSettings={appBarConfig.showSettings}
              showSupport={appBarConfig.showSupport}
              showAccountActions={appBarConfig.showAccountActions}
              isSettings={isSettings}
              toggleSideMenuText={toggleSideMenuText}
              showSideMenuText={showSideMenuText}
              toggleNavStyle={toggleNavStyle}
              useCategoryNav={useCategoryNav}
              accessibleMenuOptions={accessibleMenuOptions}
            />
          </Grid>
        ) : null}

        {/* Always render CategoryNav when using it, regardless of other conditions */}
        {useCategoryNav && !isSettings ? (
          <Grid container sx={{ flex: 1 }}>
            <Grid item>
              <CategoryNav
                isEnabled={true}
                toggleNavStyle={toggleNavStyle}
                useCategoryNav={useCategoryNav}
                toggleTheme={toggleTheme}
                currentTheme={themeType}
                showSettings={appBarConfig.showSettings}
              />
            </Grid>
            <Grid
              item
              xs
              sx={{
                height: `100vh`,
                overflowY: 'scroll',
                p: 0,
              }}
            >
              <Outlet />
            </Grid>
          </Grid>
        ) : (
          renderContent()
        )}
      </FullHeightGrid>
      {/* Global Hotkey Modal (always mounted) */}
      <HotkeyModal isOpen={hotkeyModalOpen} onClose={() => setHotkeyModalOpen(false)} options={accessibleMenuOptions} />
    </ThemeProvider>
  )
}

AppLayout.propTypes = {
  routes: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.node.isRequired,
      title: PropTypes.string.isRequired,
      route: PropTypes.string.isRequired,
      matchPath: PropTypes.string.isRequired,
    }),
  ).isRequired,
  appBarConfig: PropTypes.shape({
    showNotifications: PropTypes.bool,
    showLocationSelect: PropTypes.bool,
    showSettings: PropTypes.bool,
    showSupport: PropTypes.bool,
    showAccountActions: PropTypes.bool,
    title: PropTypes.string,
  }).isRequired,
}

export default AppLayout
